<template>
  <dl :class="$style.inputs">
    <h3 v-if="showHeading">ユーザーデータ</h3>
    <div v-for="item in items" :key="item.name" :class="$style.inputs__item">
      <dt v-if="item.value" :class="$style.inputs__name">{{ item.name }}</dt>
      <div v-if="item.value" :class="$style.inputs__values">
        <dd
          v-for="(value, index) in item.value"
          :key="index"
          :class="$style.inputs__value"
        >
          {{ value }}
        </dd>
      </div>
    </div>
  </dl>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import formatToJpy from '@/libs/utilities/formatToJpy'
import { childAgeString } from '@/libs/utilities/child'
import type { DiagnosisInputContents, Sex } from '@/types/app'
import { convertGqlGenderToSex } from '@/types/app'
import type { UserProfileInputsDiagnosisFragment } from './UserProfileInputs.generated'

const sexConversionMap: Record<Sex, string> = {
  male: '男',
  female: '女',
  noAnswer: '無回答',
}
const stringifySex = (sex: Sex): string => sexConversionMap[sex]
const stringifyChild = (
  child: DiagnosisInputContents['children'][number]
): string => `${childAgeString(child.age)}歳/${stringifySex(child.sex)}`

const stringifyExistence = (value: boolean): 'あり' | 'なし' =>
  value ? 'あり' : 'なし'

const currentYear = new Date().getFullYear()
const stringifyYearOfBirth = (year: number): string =>
  `${currentYear - year}歳(${year}年生)`

type Item = { name: string; value: Maybe<string[]> }

const props = defineProps<{
  diagnosis: UserProfileInputsDiagnosisFragment
  showHeading?: boolean
}>()

const inputs = computed((): DiagnosisInputContents => {
  const partner = {
    hasPartner: props.diagnosis.partner !== null,
    partnerYearOfBirth: props.diagnosis.partner?.birthYear,
    partnerAnnualIncome: props.diagnosis.partner?.annualIncome,
  }

  return {
    sex: convertGqlGenderToSex(props.diagnosis.gender),
    prefecture: props.diagnosis.livingPrefecture.toString(),
    yearOfbirth: props.diagnosis.birthYear,
    saving: props.diagnosis.saving,
    annualIncome: props.diagnosis.annualIncome,
    savingMonth: props.diagnosis.savingMonth,
    monthlyRentCost: props.diagnosis.monthlyRentCost,
    hasChild: props.diagnosis.children?.length > 0,
    children: props.diagnosis.children?.map(child => ({
      age: child.age,
      sex: convertGqlGenderToSex(child.gender),
    })),
    monthlyInsurance: props.diagnosis.monthlyInsurance,
    investment: props.diagnosis.investment,
    job: props.diagnosis.job?.name ?? undefined,
    loanBalance: props.diagnosis.loanBalance ?? undefined,
    ...partner,
  }
})

const aboutPartner = computed((): Item[] => {
  const partner = {
    name: '配偶者',
    value: [stringifyExistence(inputs.value.hasPartner)],
  }
  if (!inputs.value.hasPartner) {
    return [partner]
  }

  const partnerAge =
    inputs.value.partnerYearOfBirth !== undefined
      ? [
          {
            name: '配偶者の年齢',
            value: [stringifyYearOfBirth(inputs.value.partnerYearOfBirth)],
          },
        ]
      : []

  const partnerIncome =
    inputs.value.partnerAnnualIncome !== undefined
      ? [
          {
            name: '配偶者の年収',
            value: [formatToJpy(inputs.value.partnerAnnualIncome)],
          },
        ]
      : []

  return [partner, ...partnerAge, ...partnerIncome]
})

const aboutChildren = computed((): Item => {
  const children = inputs.value.hasChild
    ? inputs.value.children.map(stringifyChild)
    : ['なし']

  return {
    name: '子供',
    value: children,
  }
})

const aboutJob = computed((): Item => {
  if (inputs.value.job === undefined) {
    return {
      name: '職業',
      value: undefined,
    }
  }
  return {
    name: '職業',
    value: [inputs.value.job],
  }
})

const aboutLoanBalance = computed((): Item => {
  if (inputs.value.loanBalance === undefined) {
    return {
      name: '借入総額',
      value: undefined,
    }
  }
  return {
    name: '借入総額',
    value: [formatToJpy(inputs.value.loanBalance)],
  }
})

const items = computed((): Item[] => [
  aboutJob.value,
  {
    name: '預貯金',
    value: [formatToJpy(inputs.value.saving)],
  },
  {
    name: '年収',
    value: [formatToJpy(inputs.value.annualIncome)],
  },
  ...aboutPartner.value,
  {
    name: '毎月の貯金',
    value: [formatToJpy(inputs.value.savingMonth)],
  },
  {
    name: '毎月の住居費',
    value: [formatToJpy(inputs.value.monthlyRentCost)],
  },
  aboutChildren.value,
  {
    name: '毎月の保険料',
    value: [formatToJpy(inputs.value.monthlyInsurance)],
  },
  {
    name: '資産運用合計',
    value: [formatToJpy(inputs.value.investment)],
  },
  aboutLoanBalance.value,
])
</script>

<style lang="scss" module>
.inputs {
  padding: 18px;
  background: $color-surface-secondary;
  border-radius: $border-radius-v2-base;

  &__item {
    display: grid;
    color: $color-text-and-object-high-emphasis;
    font-size: 12px;
    grid-template: auto / 98px 1fr;
    line-height: 1;

    &:nth-child(n + 2) {
      margin-top: 20px;
    }
  }

  &__name {
    font-weight: bold;
    grid-column: col-start / 1;
    grid-row: 1;
  }

  &__values {
    grid-column: col-start 1 / 2;
    grid-row: 1;
  }

  &__value {
    text-align: right;

    &:nth-child(n + 2) {
      margin-top: 8px;
    }
  }
}
</style>
